<script setup>
import { computed } from 'vue';

const props = defineProps({
  segments: {
    type: Array,
    required: true,
    validator: arr =>
      arr.every(
        s =>
          typeof s.label === 'string' &&
          typeof s.value === 'number' &&
          typeof s.percentage === 'number' &&
          typeof s.color === 'string'
      ),
  },
  height: {
    type: String,
    default: '24px',
  },
  showLegend: {
    type: Boolean,
    default: true,
  },
  preserveOrder: {
    type: Boolean,
    default: false,
  },
});

const sortedSegments = computed(() =>
  props.preserveOrder
    ? props.segments
    : [...props.segments].sort((a, b) => b.percentage - a.percentage)
);

const totalPercentage = computed(() =>
  props.segments.reduce((sum, s) => sum + s.percentage, 0)
);

const normalizedSegments = computed(() =>
  sortedSegments.value.map(s => ({
    ...s,
    normalizedPercentage:
      totalPercentage.value > 0
        ? (s.percentage / totalPercentage.value) * 100
        : 0,
  }))
);
</script>

<template>
  <div class="w-full">
    <div class="flex w-full overflow-hidden rounded-lg" :style="{ height }">
      <div
        v-for="(segment, index) in normalizedSegments"
        :key="segment.label"
        class="transition-all duration-300"
        :class="{
          'rounded-l-lg': index === 0,
          'rounded-r-lg': index === normalizedSegments.length - 1,
        }"
        :style="{
          width: `${segment.normalizedPercentage}%`,
          backgroundColor: segment.color,
          minWidth: segment.normalizedPercentage > 0 ? '4px' : '0',
        }"
        :title="`${segment.label}: ${segment.value} (${segment.percentage}%)`"
      />
    </div>
    <div v-if="showLegend" class="mt-3 flex flex-wrap gap-4">
      <div
        v-for="segment in normalizedSegments"
        :key="segment.label"
        class="flex items-center gap-2"
      >
        <span
          class="h-3 w-3 rounded-full"
          :style="{ backgroundColor: segment.color }"
        />
        <span class="text-sm text-n-slate-12">
          {{ segment.label }}: {{ segment.value }} ({{ segment.percentage }}%)
        </span>
      </div>
    </div>
  </div>
</template>
